import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { proxies } from "../services/RepositoryService";
import { post } from "../services/HttpService";
import StarekContext from "../context/StarekContext";
import PositiveSSLSeal from "../components/PositiveSSLSeal";

function ForgotPassword() {
    const starekContext = useContext(StarekContext);

    const [workspace, setWorkspace] = useState(
        starekContext?.lastUsedWorkspace?.workspace_id || "",
    );
    const [username, setUsername] = useState("");
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!workspace.trim()) {
            setError("Please enter your workspace.");
            return;
        }
        if (!username.trim()) {
            setError("Please enter your email or username.");
            return;
        }

        setLoading(true);
        try {
            // Resolve workspace → engine URL (same pattern as StarekContext.login)
            const foundWorkspace = await proxies(workspace.trim());

            const url = `${foundWorkspace.engine.scheme}://${foundWorkspace.engine.host}:${foundWorkspace.engine.port}${process.env.REACT_APP_API_PREFIX}/${foundWorkspace.workspace_id}/auth/forgot-password`;

            await post(url, {
                username: username.trim(),
            });

            // Always show success — backend never reveals whether user exists
            setSubmitted(true);
        } catch (err) {
            // Even on network error show a generic message to avoid leaking info
            console.error("[ForgotPassword] error:", err);
            setError(
                "Something went wrong. Please check your workspace name and try again.",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-page">
            <div className="login-box">
                <div
                    className="login-logo d-flex flex-column align-items-center justify-content-center"
                    style={{ marginBottom: "1rem" }}
                >
                    <img
                        src="/dist/img/light-starek-brand-512x512.png"
                        alt="Starek"
                        className="brand-image elevation-3"
                        style={{ opacity: "0.8" }}
                    />
                </div>

                <div className="card">
                    <div className="card-body login-card-body">
                        {submitted ? (
                            /* ── Success state ── */
                            <div className="text-center py-3">
                                <div style={{ fontSize: "3rem" }}>✉️</div>
                                <h5 className="mt-3 mb-2">Check your email</h5>
                                <p className="text-muted" style={{ fontSize: "14px" }}>
                                    If an account with that username exists, we've sent a
                                    password reset link to the associated email address.
                                    The link expires in 60 minutes.
                                </p>
                                <p className="text-muted" style={{ fontSize: "13px" }}>
                                    Didn't receive it? Check your spam folder or{" "}
                                    <button
                                        className="btn btn-link p-0 align-baseline"
                                        style={{ fontSize: "13px" }}
                                        onClick={() => setSubmitted(false)}
                                    >
                                        try again
                                    </button>
                                    .
                                </p>
                                <Link
                                    to="/login"
                                    className="btn btn-outline-secondary btn-sm mt-2"
                                >
                                    Back to Sign In
                                </Link>
                            </div>
                        ) : (
                            /* ── Form state ── */
                            <>
                                <p className="login-box-msg mb-3">
                                    Enter your workspace and email to receive a
                                    password reset link.
                                </p>

                                {error && (
                                    <div className="alert alert-danger py-2 px-3 mb-3" style={{ fontSize: "14px" }}>
                                        {error}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} noValidate>
                                    {/* Workspace */}
                                    <div className="input-group mb-3">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Workspace"
                                            value={workspace}
                                            onChange={(e) =>
                                                setWorkspace(e.target.value)
                                            }
                                            autoComplete="organization"
                                            required
                                        />
                                        <div className="input-group-append">
                                            <div className="input-group-text">
                                                <span className="fas fa-globe" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Email / Username */}
                                    <div className="input-group mb-3">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Email or Username"
                                            value={username}
                                            onChange={(e) =>
                                                setUsername(e.target.value)
                                            }
                                            autoComplete="username"
                                            required
                                        />
                                        <div className="input-group-append">
                                            <div className="input-group-text">
                                                <span className="fas fa-envelope" />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-6">
                                            <Link
                                                to="/login"
                                                className="btn btn-outline-secondary btn-block"
                                            >
                                                Back to Sign In
                                            </Link>
                                        </div>
                                        <div className="col-6">
                                            <button
                                                type="submit"
                                                className="btn btn-primary btn-block"
                                                disabled={loading}
                                            >
                                                {loading ? (
                                                    <>
                                                        <span
                                                            className="spinner-border spinner-border-sm me-1"
                                                            role="status"
                                                            aria-hidden="true"
                                                        />
                                                        Sending…
                                                    </>
                                                ) : (
                                                    "Send Reset Link"
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </form>

                                <hr className="my-3" />
                                <p
                                    className="text-muted text-center mb-0"
                                    style={{ fontSize: "12px" }}
                                >
                                    SSO users (Sign in with Starek / Google) must
                                    reset their password at{" "}
                                    <a
                                        href={process.env.REACT_APP_STAREK_AUTH_URI}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        auth.starek.in
                                    </a>
                                    .
                                </p>
                            </>
                        )}
                    </div>
                </div>

                <div className="text-white" style={{ textAlign: "center" }}>
                    <br />
                    Structured Telemetry and Reporting
                </div>
                <PositiveSSLSeal />
            </div>
        </div>
    );
}

export default ForgotPassword;
